/**
 * Shared card recipes: the class strings that give a card its frame, so the
 * surfaces that sit side by side in one list cannot drift apart.
 *
 * Extracted 2026-09-15 when affiliate cards joined the professional cards on
 * /search, under articles and in the chat (`affiliate-links-spec.md` §6.4).
 * The first affiliate cards copied these strings by hand, which is exactly how
 * the category tag ended up hand-rolled five times (see `badge-styles.ts`).
 * Moving them here changed no pixel on the professional cards: computed styles
 * were compared before and after on /search, an article's pros row and the
 * chat preview, at 1280 and 375 px, with a control run each.
 *
 * Mirrors the `badge-styles.ts` idiom: plain exported class strings, no runtime.
 * Each recipe is the FRAME only; content-specific classes stay at the call site.
 */

// ── Search result list card (`ProfessionalListCard`, `AffiliateListCard`) ────

/** The outer <article>. */
export const LIST_CARD =
  "group overflow-hidden rounded-card border border-card-line bg-white shadow-e1 transition-[box-shadow,border-color] duration-150 hover:border-navy/15 hover:shadow-e2";

/** The whole-card link: photo well | body | action column from `lg`. */
export const LIST_CARD_LINK =
  "grid grid-cols-[104px_minmax(0,1fr)] items-start transition-colors duration-150 hover:bg-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold focus-visible:outline-offset-[-2px] sm:grid-cols-[200px_minmax(0,1fr)] sm:items-stretch lg:grid-cols-[180px_minmax(0,1fr)_200px]";

/**
 * The well's OUTER box: carries the gutter and positions the tab. The inner
 * box clips; the tab must be a sibling of it (`badge-styles.ts` BADGE_TAB).
 */
export const LIST_CARD_WELL = "relative m-8 sm:m-13 sm:self-start";

/** Where the category tab sits on a list card. Prefix with `BADGE_TAB_CATEGORY`. */
export const LIST_CARD_TAB =
  "absolute -left-8 bottom-8 min-w-min max-w-[calc(70%+8px)] leading-tight sm:-left-13 sm:bottom-13 sm:max-w-[calc(70%+13px)]";

/** The body column. */
export const LIST_CARD_BODY = "flex min-w-0 flex-col px-8 pt-13 pb-21 sm:px-21";

/** The uppercase meta line above the name (location, languages, experience). */
export const LIST_CARD_META =
  "meta-line flex flex-wrap items-center gap-x-[6px] gap-y-2 text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500";

/** One icon + text item on the meta line. */
export const LIST_CARD_META_ITEM = "flex shrink-0 items-center gap-[4px]";

/** The name heading. */
export const LIST_CARD_NAME =
  "mt-8 flex items-center gap-8 font-serif text-[1.15rem] leading-tight text-navy sm:text-[1.5rem]";

/** The line under the name (title, or an affiliate's "What it's for"). */
export const LIST_CARD_SUBTITLE = "mt-3 text-xs text-gray-500 sm:text-sm";

/** The third column, from `lg`. */
export const LIST_CARD_ACTION_COLUMN = "hidden flex-col items-end justify-between px-21 py-21 text-right lg:flex";

/** The outlined action ("View profile", "Visit site") that fills on card hover. */
export const LIST_CARD_ACTION =
  "rounded-control border border-navy/15 px-13 py-8 text-[11px] font-semibold uppercase tracking-[0.08em] text-navy transition-all duration-150 group-hover:border-navy group-hover:bg-navy group-hover:text-white";

// ── Grid card (`ProfessionalCard`, `AffiliateGridCard`) ─────────────────────

/**
 * Where the category tab sits on a grid card. The negative inset IS the card's
 * own padding, so it differs by density. Prefix with `BADGE_TAB_CATEGORY`.
 */
export const GRID_CARD_TAB = {
  compact: "absolute bottom-13 min-w-min leading-tight -left-13 max-w-[calc(70%+13px)]",
  full: "absolute bottom-13 min-w-min leading-tight -left-21 max-w-[calc(70%+21px)]",
} as const;

/**
 * The name's line height: FIXED, independent of the fitted font size, so the
 * name row is one height on every card and the lines below align across a row.
 */
export const GRID_CARD_NAME_LEADING = { compact: "leading-[22px]", full: "leading-[28px]" } as const;

/**
 * SSR/no-JS fallback size for a grid card name, by character count. The real
 * size is fitted to the card in `ProfessionalCard`; this keeps the first paint
 * close to it. Calibrated to the rendered card widths.
 */
export function gridCardNameSize(len: number, compact: boolean): string {
  if (compact) return len <= 15 ? "text-base" : len <= 20 ? "text-sm" : len <= 25 ? "text-xs" : "text-[11px]";
  return len <= 19 ? "text-xl" : len <= 22 ? "text-lg" : len <= 26 ? "text-base" : "text-sm";
}

/**
 * Language codes a card's meta row shows before folding the rest into "+N".
 * The grid card renders this many on the server and then fits the row to its
 * measured width (`ProfessionalCard`); the phone card wraps its meta row and
 * uses it as a plain cap. 2 is what fits the narrowest grid card (192px, at
 * lg and xl) beside "HCMC +1". Uncapped, a pro with six languages ran off the
 * footer mid-code.
 */
export const CARD_LANG_CODES_SHOWN = 2;

/**
 * The ruled uppercase footer under the title, shared by the pro and affiliate
 * grid cards. ⛔ NOT clipped: both cards raise tooltips out of it (language
 * names, the affiliate tag), and any clip cut them off. `overflow-hidden` cut
 * every language tooltip at the top; `overflow-x-clip` still cut a centred one
 * at the side on /vi, where the names run longer. Nothing needs the clip: the
 * pro card fits its language codes to the row's measured width.
 */
export const GRID_CARD_FOOTER =
  "mt-13 flex items-center gap-x-13 border-t border-navy/15 pt-13 text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500";

// ── Chat recommendation row (`ProRecommendationList`, `AffiliateRecommendationList`) ──

/**
 * The row. The WHOLE row is a link (owner, 2026-09-15): the name link carries
 * `CHAT_ROW_LINK`, whose `::before` stretches over the row. Anything that must
 * stay separately clickable or hoverable (the action pill, a badge tooltip)
 * is positioned and comes AFTER the name link in the DOM, so it paints above
 * that overlay.
 */
export const CHAT_ROW =
  "group relative flex items-center gap-13 rounded-pop border border-card-line bg-white p-8 pr-13 transition-[border-color,box-shadow] duration-150 hover:border-navy/15 hover:shadow-e2";

/** The name link, stretched over the row; its focus ring outlines the row. */
export const CHAT_ROW_LINK =
  "group-hover:underline before:absolute before:inset-0 before:rounded-pop before:content-[''] focus-visible:outline-none focus-visible:before:outline focus-visible:before:outline-2 focus-visible:before:outline-offset-2 focus-visible:before:outline-gold";

/**
 * The slow zoom on a card's photo while the card is hovered. Needs a `group`
 * ancestor (the card or its link) and a clipping well. The resting
 * `scale-100` is load-bearing: Tailwind v4 animates the `scale` property, which
 * cannot interpolate from `none`.
 */
export const PHOTO_HOVER_ZOOM =
  "motion-safe:scale-100 motion-safe:transition-[scale] motion-safe:duration-700 motion-safe:ease-[cubic-bezier(0.4,0,0.2,1)] motion-safe:group-hover:scale-[1.06]";

/** The portrait well's size (3:4, the canonical photo ratio). */
export const CHAT_ROW_WELL_SIZE = "h-[64px] w-[48px]";

/** The name line. */
export const CHAT_ROW_NAME = "truncate font-serif text-[1rem] leading-tight text-navy";

/** The small meta line's type. */
export const CHAT_ROW_META_TYPE = "text-[0.7rem] text-gray-500";

/**
 * The action pill's shape. 28px tall because a taller one dominated the row
 * (owner call 2026-08-21), which is under the WCAG 2.5.5 target size, so a
 * transparent `::after` centred on the pill carries a 44px tap area. Colours
 * are added at the call site: solid navy for "Contact", outlined for an
 * external "Visit site".
 */
export const CHAT_ROW_PILL =
  "relative inline-flex min-h-[28px] items-center whitespace-nowrap rounded-full px-13 text-xs font-semibold after:absolute after:inset-x-0 after:top-1/2 after:h-[44px] after:-translate-y-1/2 after:content-[''] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold";

/**
 * A story card that is one link (the homepage insights and guides rows): the
 * `group` its photo zoom and title hover key off, and a gold focus ring that
 * follows the card's own corners. Extracted 2026-09-16 at its second user.
 */
export const STORY_LINK =
  "group block rounded-card focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold";

/** Uppercase meta lettering without a size: weight, case and the 0.08em
 *  tracking every meta line shares. Add the size and colour at the call site. */
export const META_CASE = "font-semibold uppercase tracking-[0.08em]";

/** A story card's uppercase meta text (author name, date), defined once so its
 *  tracking is too. Add the colour at the call site. */
export const STORY_META = `text-xs ${META_CASE}`;

/**
 * A story card's title on hover. An underline says "this is a link" without
 * depending on colour, so it survives low contrast and colour blindness.
 *
 * ⛔ THE OFFSET IS BOUND BY `line-clamp`. A clamped title clips to its own box,
 * so the LAST visible line's underline must end inside that line: measured on a
 * 16px title with 20px lines, the baseline sits 15.5px down, and at offset 4 a
 * 2px underline ran from 19.5px to 21.5px, leaving 0.5px on screen. The last
 * line read as thinner than the rest (owner, 2026-09-16). Offset 2 ends it at
 * 19.5px. Letting it paint outside the box (`overflow-clip-margin`) was tried
 * and rejected: the tops of the hidden next line showed through.
 */
export const STORY_TITLE_HOVER = "decoration-2 underline-offset-2 group-hover:underline";
